import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core'
import { FormBuilder, FormGroup, Validators } from '@angular/forms'
import { LoadingController, ModalController } from '@ionic/angular'
import { CompliancePage } from '@modals/docs/compliance/compliance.page'
import { ConsentPage } from '@modals/docs/consent/consent.page'
import { FarmacoPage } from '@modals/docs/farmaco/farmaco.page'
import { TermsPage } from '@modals/docs/terms/terms.page'
import { TranslateService } from '@ngx-translate/core'
import { ApiService } from '@services/api/api.service'
import { EndpointsService } from '@services/api/endpoints.service'
import { MedicosService } from '@services/users/medicos/medicos.service'
import { AlertService } from '@services/utils/alerts/alert.service'
import { ValidationMessages } from '@services/utils/validations/validation-messages.service'
import { ValidationService } from '@services/utils/validations/validation.service'
import { IonicSelectableComponent } from 'ionic-selectable'
import { first } from 'rxjs/operators'
import { ConfirmedValidator } from 'src/app/components/features/validations/confirmed.validator'

@Component({
  selector: 'app-register',
  encapsulation: ViewEncapsulation.None,
  templateUrl: './register.page.html',
  styleUrls: ['./register.page.scss']
})
export class RegisterPage implements OnInit {
  @Input() data: any
  createForm: FormGroup
  submitted: boolean = false
  errorsMessages: string[] = []
  createMedico: any = []
  specialties = []
  eyeImage: string[] = ['eye-off', 'eye-off']
  typeInput: string[] = ['text', 'text'] // Password fields are set to text type by default to prevent auto fill

  inputValue?: string
  options: string[] = []
  specialty_name
  rols = []
  id_country
  country_name = ''
  states = []
  cities = []
  status_city: boolean = true
  statusForm = false
  permissions = [false, false]
  validationId = 'onlyNumber'
  validationReqId = true
  validationReqPrefix = true
  validationMessages = this.validationMessagesLoad.validationMessages

  url = this.endpointsService.url

  constructor(
    private endpointsService: EndpointsService,
    private apiService: ApiService,
    public formBuilder: FormBuilder,
    private medicosService: MedicosService,
    private alertService: AlertService,
    private validationService: ValidationService,
    public loadingController: LoadingController,
    public modalController: ModalController,
    private translate: TranslateService,
    private validationMessagesLoad: ValidationMessages
  ) {}

  ngOnInit() {
    this.loadLanguage(this.translate.currentLang)
    this.loadSpecialties()
    this.validateForm()
    this.loadStates()
  }

  enableForm() {
    this.statusForm = true
    this.enableFields(this.translate.currentLang)
    this.validateField()
  }

  enableFields(country_name) {
    let data = this.createForm.controls
    let rol_id = data.rol_id.value

    switch (country_name) {
      case 'es_Ar':
        if (rol_id == 3 || rol_id == 7 || rol_id == 11) {
          this.permissions = [true, true]
        } else {
          this.permissions = [false, false]
        }
        this.validationId = 'onlyNumber'
        this.validationReqId = true
        break
      case 'es_Ch':
        if (rol_id == 3 || rol_id == 7 || rol_id == 11) {
          this.permissions = [true, true]
        } else {
          this.permissions = [false, false]
        }
        this.validationId = 'onlyNumberGuion'
        this.validationReqId = false

        break
      case 'es_Pa':
        if (rol_id == 3 || rol_id == 7 || rol_id == 11) {
          this.permissions = [true, true]
        } else {
          this.permissions = [false, false]
        }
        this.validationId = 'onlyNumber'
        this.validationReqId = true
        break
      default:
        console.log('No hay lenguaje: ' + country_name)
    }
  }

  onInput(e: Event): void {
    const value = (e.target as HTMLInputElement).value
    if (!value || value.indexOf('@') >= 0) {
      this.options = []
    } else {
      this.options = ['gmail.com', 'hotmail.com', 'outlook.com', 'yahoo.com'].map((domain) => `${value}@${domain}`)
    }
  }

  ionViewDidEnter() {
    this.typeInput = ['password', 'password'] // When the page has loaded the password fields are set to password type
  }

  validateForm() {
    this.createForm = this.formBuilder.group(
      {
        rol_id: ['', Validators.compose([Validators.required])],
        first_name: ['', [this.validationService.Validation(2, 51, 'onlyLetter')]],
        last_name: ['', [this.validationService.Validation(3, 51, 'onlyLetter')]],
        email: ['', [this.validationService.Validation(6, 51, 'onlyEmail')]],
        password: ['', [this.validationService.Validation(6, 51, 'onlyPassword')]],
        confirm_password: ['', Validators.compose([Validators.required])],
        country_code_phone: [9, [this.validationService.Validation(1, 4, 'onlyNumber')]],
        prefix: ['', [this.validationService.Validation(1, 7, 'onlyNumber')]],
        phone_number: ['', [this.validationService.Validation(6, 16, 'onlyNumber')]],
        prefix_2: ['', [this.validationService.Validation(1, 7, 'onlyNumber', false)]],
        phone_number_2: ['', [this.validationService.Validation(3, 31, 'onlyNumber', false)]],
        tuition: ['', [this.validationService.Validation(3, 16, this.validationId, this.validationReqId)]],
        ced_user: ['', [this.validationService.Validation(3, 16, 'onlyNumbersText')]],
        specialty_name: ['', Validators.compose([Validators.required])],
        state_id: ['', Validators.compose([Validators.required])],
        city_id: [
          {
            value: '',
            disabled: true
          },
          Validators.compose([Validators.required])
        ],
        agreeTerms: [false, Validators.requiredTrue],
        agreePrivacy: [false, Validators.requiredTrue],
        agreeCompliance: [false, Validators.requiredTrue],
        agreePharm: [false]
      },
      {
        validator: ConfirmedValidator('password', 'confirm_password')
      }
    )
  }

  validateField() {
    let data = this.createForm.controls
    let rol_id = data.rol_id.value

    this.createForm.controls['tuition'].setValidators([
      this.validationService.Validation(3, 16, this.validationId, this.validationReqId)
    ])
    this.createForm.controls['tuition'].updateValueAndValidity()

    this.createForm.controls['prefix'].setValidators([
      this.validationService.Validation(1, 7, this.validationId, this.validationReqPrefix)
    ])
    this.createForm.controls['prefix'].updateValueAndValidity()
    this.validateSpecialty(rol_id)
  }

  validateSpecialty(rol_id) {
    if (rol_id == 3 || rol_id == 7 || rol_id == 11) {
      this.createForm.controls['specialty_name'].setValidators([Validators.compose([Validators.required])])
      this.createForm.controls['specialty_name'].updateValueAndValidity()
    } else {
      this.createForm.controls['specialty_name'].clearValidators()
      this.createForm.controls['specialty_name'].updateValueAndValidity()
      this.createForm.controls['tuition'].clearValidators()
      this.createForm.controls['tuition'].updateValueAndValidity()
    }
  }

  loadStates() {
    this.apiService
      .getAll(this.url.state)
      .pipe(first())
      .subscribe(
        (res) => {
          this.states = res.filter((obj) => {
            return obj.active && obj.country_id == this.id_country
          })
        },
        (err) => {}
      )
  }

  loadCities() {
    let data = this.createForm.controls
    let state = data.state_id.value.id
    let stateSelected = []
    stateSelected = this.states.filter((s) => s.id == state).map((s) => s.country_name)
    this.country_name = stateSelected.length > 0 ? stateSelected[0] : ''
    this.createForm.get('city_id').enable()
    this.createForm.controls['city_id'].setValue('')
    if (this.validationReqPrefix) {
      this.createForm.controls['prefix'].setValue('')
      this.createForm.controls['prefix_2'].setValue('')
    }

    this.apiService
      .getAll(this.url.city)
      .pipe(first())
      .subscribe(
        (res) => {
          this.cities = res.filter((obj) => {
            return obj.active && obj.state_id == state
          })
          this.status_city = false
        },
        (err) => {}
      )
  }

  showDetailsError() {
    if (!this.createForm.valid) {
      this.createForm.markAllAsTouched()
    }
  }

  get errorControl() {
    return this.createForm.controls
  }

  changeImage(i: number) {
    if (this.eyeImage[i] == 'eye-off') {
      this.eyeImage[i] = 'eye'
      this.typeInput[i] = 'text'
      setTimeout(() => {
        this.eyeImage[i] = 'eye-off'
        this.typeInput[i] = 'password'
      }, 2000)
    } else {
      this.eyeImage[i] = 'eye-off'
      this.typeInput[i] = 'password'
    }
  }

  loadLanguage(country_name) {
    const countryLanguages = {
      es_Ar: { id: 1, validationReqPrefix: true },
      es_Ch: { id: 2, validationReqPrefix: false },
      es_Pa: { id: 3, validationReqPrefix: false }
    }

    const language = countryLanguages[country_name]
    if (language) {
      this.id_country = language.id
      this.validationReqPrefix = language.validationReqPrefix
      this.loadRol()
    } else {
      console.log(`No hay lenguaje: ${country_name}`)
    }
  }

  loadRol() {
    this.medicosService
      .getRols(this.id_country)
      .pipe(first())
      .subscribe(
        (res) => (this.rols = res.data),
        (err) => console.log(err)
      )
  }

  loadSpecialties() {
    this.apiService
      .getAll(this.url.specialty)
      .pipe(first())
      .subscribe(
        (res) => {
          const filterSpecialities = res.filter( s => s.name != 'No suministrado')
          this.specialties = filterSpecialities
        },
        (err) => {}
      )
  }

  loadPrefix() {
    let data = this.createForm.controls
    if (this.validationReqPrefix) {
      this.createForm.controls['prefix'].setValue(data.city_id.value.code)
      this.createForm.controls['prefix_2'].setValue(data.city_id.value.code)
    }
  }

  portChange(event: { component: IonicSelectableComponent; value: any }) {
    this.specialty_name = event
  }

  async create() {
    this.submitted = true
    await this.alertService.presentLoading()
    this.medicosService
      .create(this.getData())
      .pipe(first())
      .subscribe(
        (res) => {
          this.alertService.dismiss().then(() => {
            this.success(res)
          })
        },
        (err) => {
          this.alertService.dismiss().then(() => {
            this.handleError(err)
          })
        }
      )
  }

  private handleError(err: any) {
    const errorId = err?.payload?.data?.id
    
    switch (errorId) {
      case -105:
        this.alertService.statusAlert(
          'Verificación Requerida',
          'Se ha enviado un correo para validar su cuenta. Por favor revise su bandeja de entrada.',
          () => this.modalController.dismiss()
        )
        break
      case -133:
      case -141:
        this.alertService.statusAlert(
          'Registro Fallido',
          'No fue posible hacer el registro. Si el problema persiste, contacta a nuestro equipo de soporte. soporte@tbtbglobal.com'
        )
        break
      case -145:
        this.alertService.statusAlert(
          'Registro Fallido',
          err.payload.message,
          () => this.modalController.dismiss()
        )
        break
      default:
        this.alertService.statusAlert('Registro Fallido', err.payload.message)
        break
    }
  }

  getData() {
    let data = this.createForm.controls
    this.createMedico = {
      first_name: data.first_name.value,
      last_name: data.last_name.value,
      country_code_phone: '' + data.country_code_phone.value,
      prefix: data.prefix.value,
      phone_number: data.phone_number.value,
      prefix_2: data.phone_number_2.value != '' ? data.prefix_2.value : '',
      phone_number_2: data.phone_number_2.value,
      specialty_name: this.permissions[0] ? data.specialty_name.value.name : '',
      city_id: data.city_id.value.id,
      identification: data.ced_user.value,
      tuition: data.tuition.value,
      rol_id: data.rol_id.value,
      email: data.email.value,
      password: data.password.value,
      country_name: this.country_name,
      state_name: data.city_id.value.states_name,
      city_name: data.city_id.value.name,
      status_app: 1,
      forgot_password: false
    }
    return this.createMedico
  }

  getStringId(value: string) {
    return value.length >= 2 ? value.substring(0, 2) : value[0] + ' '
  }

  success(res) {
    if (res.error) {
      this.error()
    } else {
      const subHeader = 'Registro exitoso!'
      const message = 'Se ha registrado el médico con éxito'
      this.alertService.statusAlert(subHeader, message)
      this.modalController.dismiss()
    }
  }

  error() {
    const subHeader = 'Registro fallido!'
    const message = 'No se ha registrado el médico con éxito, por favor vuelva a intentar'
    this.alertService.statusAlert(subHeader, message)
  }

  async Terms() {
    const modal = await this.modalController.create({
      component: TermsPage,
      cssClass: 'forgot-password'
    })
    return await modal.present()
  }

  async Consent() {
    const modal = await this.modalController.create({
      component: ConsentPage,
      cssClass: 'forgot-password'
    })
    return await modal.present()
  }

  async Compliance() {
    const modal = await this.modalController.create({
      component: CompliancePage,
      cssClass: 'forgot-password'
    })
    return await modal.present()
  }

  async Farmaco() {
    const modal = await this.modalController.create({
      component: FarmacoPage,
      cssClass: 'forgot-password'
    })
    return await modal.present()
  }

  async closeModal() {
    await this.modalController.dismiss()
  }
}
